import { compareAsc, isBefore, subHours } from 'date-fns';
import find from 'lodash/find';
import orderBy from 'lodash/orderBy';
import { nanoid } from 'nanoid';
import { computed, ComputedRef, onBeforeUnmount, Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import BigNumber from '@exchange/helpers/bignumber';
import persistanceHelper from '@exchange/helpers/persistance-helper';
import { ordersService } from '@exchange/libs/order/my-orders/service/src';
import type { ReactiveOrdersData } from '@exchange/libs/order/my-orders/service/src';
import { MyOrdersListType, MyOrdersTabs, OrderStatus } from '@exchange/libs/order/shared-model/src/lib/order-essentials';
import { settingsService } from '@exchange/libs/settings/service/src';
import { launchdarkly } from '@exchange/libs/utils/launchdarkly/src';

import { DivTableColumn, createColumnInstance, divTableSortDirections, createDivTableColumnInstance, type DivTableSortDirection } from './components/div-table-helpers';

interface TableSorting {
  sorted: string;
  sortDirection: DivTableSortDirection;
}

const getTimeFieldForSorting = (type: MyOrdersListType) => (type === MyOrdersListType.open ? 'time' : 'timeLastUpdated');

const compareDates = (a: string | number, b: string | number) => compareAsc(new Date(a), new Date(b));
const compareStrings = (a: string, b: string) => {
  if (a === b) {
    return 0;
  }

  if (a > b) {
    return 1;
  }

  return -1;
};
const getOrdersValues = (type: MyOrdersListType, prop: string, a: ReactiveOrdersData, b: ReactiveOrdersData) => {
  const v1 = ordersService.getOrderById(a.orderId, type)?.[prop];
  const v2 = ordersService.getOrderById(b.orderId, type)?.[prop];

  return { v1, v2 };
};
const sortStringsBy = (type: MyOrdersListType, prop: string) => (a: ReactiveOrdersData, b: ReactiveOrdersData) => {
  const { v1, v2 } = getOrdersValues(type, prop, a, b);

  return compareStrings(v1, v2);
};
const sortBigNumberValuesBy = (type: MyOrdersListType, prop: string) => (a: ReactiveOrdersData, b: ReactiveOrdersData) => {
  const { v1, v2 } = getOrdersValues(type, prop, a, b);

  return (v1 || new BigNumber(0)).comparedTo(v2 || new BigNumber(0));
};
const sortByDisplayPrice = (type: MyOrdersListType) => (a: ReactiveOrdersData, b: ReactiveOrdersData) => {
  const v1 = ordersService.getOrderById(a.orderId, type)?.displayPrice;

  return v1 ? sortBigNumberValuesBy(type, 'displayPrice')(a, b) : -1;
};
const sortByTime = (type: MyOrdersListType) => (a: ReactiveOrdersData, b: ReactiveOrdersData) => {
  const time = getTimeFieldForSorting(type);

  const { v1, v2 } = getOrdersValues(type, time, a, b);

  return compareDates(v1, v2);
};

const getInitialTableSorting = (type: MyOrdersListType, tableSortingKey?: string) => {
  const defaultSorting = {
    sorted: getTimeFieldForSorting(type),
    sortDirection: divTableSortDirections.desc,
  };

  if (!tableSortingKey) {
    return defaultSorting;
  }

  const v = persistanceHelper.getObjFromJSON(tableSortingKey, undefined) as TableSorting | undefined;

  if (!v) {
    return defaultSorting;
  }

  return v;
};

const useThrottleWaitNumbers = (type: MyOrdersListType, onUpdate: () => void, getOrdersDataUpdate: () => string) => {
  let interval;
  let ordersDataUpdate: string | undefined;

  const throttleWaitNumber = computed(() =>
    Math.max(type === MyOrdersListType.open ? launchdarkly.flags['throttle-ws'].value['my-orders-open'] : launchdarkly.flags['throttle-ws'].value['my-orders-filled'], 100),
  );

  const setListUpdates = () => {
    ordersDataUpdate = getOrdersDataUpdate();

    interval = setInterval(() => {
      const nId = getOrdersDataUpdate();

      if (ordersDataUpdate !== nId) {
        ordersDataUpdate = nId;
        onUpdate();
      }
    }, throttleWaitNumber.value);
  };

  watch(throttleWaitNumber, (newV, oldV) => {
    if (oldV === 0 && newV !== 0) {
      clearInterval(interval);
      setListUpdates();
    }
  });

  onBeforeUnmount(() => {
    clearInterval(interval);
  });

  return {
    setListUpdates,
  };
};

export const useOrdersListActive = (marketId: Ref<string>) => {
  const myOrdersSettings = computed(() => settingsService.settings.userSettings.myOrdersSettings);
  let ordersDataUpdateId = nanoid();

  const getOrdersDataUpdateId = () => ordersDataUpdateId;

  const activeOrdersData = computed(() =>
    orderBy(
      ordersService.reactiveData[MyOrdersListType.open].value,
      (data: ReactiveOrdersData) => {
        const { counter, orderId } = data;
        const makeVueAware = Number(counter >= 0);

        const order = ordersService.getOrderById(orderId, MyOrdersListType.open);

        // Reference counter to make Vue aware of such dependency
        return order?.time.getTime() || makeVueAware;
      },
      ['desc'],
    ),
  );

  watch(
    activeOrdersData,
    () => {
      ordersDataUpdateId = nanoid();
    },
    { immediate: true },
  );

  watch(marketId, () => {
    ordersDataUpdateId = nanoid();
  });

  const getActiveOrdersDataToShow = () => {
    let returnValue = activeOrdersData.value;
    const showAllMarkets = myOrdersSettings.value.showOrdersFromAllMarkets;

    if (!showAllMarkets && marketId.value) {
      returnValue = returnValue.filter((item) => item.instrumentCode === marketId.value);
    }

    const filterActiveOrdersDataByStatuses = (statuses: Array<OrderStatus>) =>
      returnValue.filter((item) => {
        const o = ordersService.getOrderById(item.orderId, MyOrdersListType.open);
        return o?.status && !statuses.includes(o?.status);
      });

    if (myOrdersSettings.value.hideCancelledOrders && myOrdersSettings.value.hideFilledOrders) {
      returnValue = filterActiveOrdersDataByStatuses([OrderStatus.CLOSED, OrderStatus.FILLED_CLOSED, OrderStatus.FILLED_FULLY]);
    } else if (myOrdersSettings.value.hideCancelledOrders) {
      returnValue = filterActiveOrdersDataByStatuses([OrderStatus.CLOSED, OrderStatus.FILLED_CLOSED]);
    } else if (myOrdersSettings.value.hideFilledOrders) {
      returnValue = filterActiveOrdersDataByStatuses([OrderStatus.FILLED_FULLY]);
    }

    const perpsOrders = returnValue.filter((item) => item.instrumentCode.endsWith('_P'));
    const spotOrders = returnValue.filter((item) => !item.instrumentCode.endsWith('_P'));

    return {
      allOrders: returnValue,
      perpsOrders,
      spotOrders,
    };
  };

  const getLimitedActiveOrdersDataToShow = (orders: Array<ReactiveOrdersData>) => {
    const displayLimit = launchdarkly.flags['my-orders-open-limit'].value;

    return orders.slice(0, displayLimit);
  };

  const activeOrdersDataToShow = ref(getActiveOrdersDataToShow());

  const updateListActive = () => {
    activeOrdersDataToShow.value = getActiveOrdersDataToShow();
  };

  const { setListUpdates } = useThrottleWaitNumbers(MyOrdersListType.open, updateListActive, getOrdersDataUpdateId);

  setListUpdates();

  watch(myOrdersSettings, () => {
    activeOrdersDataToShow.value = getActiveOrdersDataToShow();
  });

  return {
    getLimitedActiveOrdersDataToShow,
    activeOrdersDataToShow,
    getOrderById: (orderId) => ordersService.getOrderById(orderId, MyOrdersListType.open),
  };
};

export const useOrdersListInactive = (marketId: Ref<string>) => {
  const myOrdersSettings = computed(() => settingsService.settings.userSettings.myOrdersSettings);
  let ordersDataUpdateId = nanoid();

  const getOrdersDataUpdateId = () => ordersDataUpdateId;

  const inactiveOrdersData = computed(() =>
    orderBy(
      ordersService.reactiveData[MyOrdersListType.filled].value,
      (data: ReactiveOrdersData) => {
        const { counter, orderId } = data;
        const makeVueAware = Number(counter >= 0);

        // Reference counter to make Vue aware of such dependency
        return ordersService.getOrderById(orderId, MyOrdersListType.filled)?.timeLastUpdated?.getTime() || makeVueAware;
      },
      ['desc'],
    ),
  );

  watch(
    inactiveOrdersData,
    () => {
      ordersDataUpdateId = nanoid();
    },
    { immediate: true },
  );

  watch(marketId, () => {
    ordersDataUpdateId = nanoid();
  });

  const getInactiveOrdersDataToShow = () => {
    const filterDate = subHours(new Date(), 24);
    let returnValue = inactiveOrdersData.value;
    const showAllMarkets = myOrdersSettings.value.showOrdersFromAllMarkets;

    if (!showAllMarkets && marketId.value) {
      returnValue = returnValue.filter((item) => item.instrumentCode === marketId.value);
    }

    return returnValue.filter((item) => {
      const o = ordersService.getOrderById(item.orderId, MyOrdersListType.filled);

      if (!o) {
        return 0;
      }

      return isBefore(filterDate, o.timeLastUpdated?.getTime() ?? o.time?.getTime() ?? 0);
    });
  };

  const inactiveOrdersDataToShow = ref(getInactiveOrdersDataToShow());

  const updateListInactive = () => {
    inactiveOrdersDataToShow.value = getInactiveOrdersDataToShow();
  };

  const { setListUpdates } = useThrottleWaitNumbers(MyOrdersListType.filled, updateListInactive, getOrdersDataUpdateId);

  setListUpdates();

  watch(myOrdersSettings, () => {
    inactiveOrdersDataToShow.value = getInactiveOrdersDataToShow();
  });

  return {
    inactiveOrdersDataToShow,
    getOrderById: (orderId) => ordersService.getOrderById(orderId, MyOrdersListType.filled),
  };
};

export const useOrdersListMobile = () => {
  const { t } = useI18n({ useScope: 'global' });

  const mobileHeader = computed(() => [
    createColumnInstance({
      title: t('modules.myOrders.header.symbol'),
      columnClass: 'symbol',
    }),
    createColumnInstance({
      title: `${t('modules.myOrders.header.price')} / ${t('modules.myOrders.header.amount')}`,
      columnClass: 'price',
    }),
  ]);

  return {
    mobileHeader,
    cancelOrder: ordersService.cancelOrder.bind(ordersService),
  };
};

export const useOrdersListDesktop = (type: MyOrdersListType, tableSortingKey: string, currentBreakPoint: Ref<string>, showOrderType: Ref<boolean>) => {
  const { t } = useI18n({ useScope: 'global' });

  const tableSorting = ref<TableSorting>(getInitialTableSorting(type, tableSortingKey));

  const computedTableSorting = computed({
    get: () => tableSorting.value,
    set: (value) => {
      persistanceHelper.localstorageSet(tableSortingKey, value);
      tableSorting.value = value;
    },
  });

  const getSorted = (name: string) => tableSorting.value?.sorted.includes(name);

  const getSortDirection = (name: string) => (tableSorting.value?.sorted.includes(name) ? tableSorting.value.sortDirection : undefined);

  const saveSorting = (event: Array<DivTableColumn<ReactiveOrdersData>>) => {
    const obj = find(event, ['sorted', true]);

    if (!obj) {
      return;
    }

    computedTableSorting.value = {
      sorted: obj.columnClass,
      sortDirection: obj.sortDirection,
    };
  };

  const desktopHeader = computed(() => [
    createDivTableColumnInstance({
      title: t('modules.myOrders.header.time'),
      columnClass: 'time align-left',
      sortFunction: sortByTime(type),
      sorted: getSorted('time'),
      sortDirection: getSortDirection('time'),
      hidden: currentBreakPoint.value.includes('hide-time'),
    }),
    createDivTableColumnInstance({
      title: t('modules.myOrders.header.symbol'),
      columnClass: 'market align-left',
      sortFunction: sortStringsBy(type, 'instrumentCode'),
      sorted: getSorted('market'),
      sortDirection: getSortDirection('market'),
      hidden: false,
    }),
    createDivTableColumnInstance({
      title: t('modules.myOrders.header.side'),
      columnClass: 'side align-left',
      sortFunction: sortStringsBy(type, 'side'),
      sorted: getSorted('side'),
      sortDirection: getSortDirection('side'),
      hidden: false,
    }),
    ...(showOrderType.value
      ? [
          createDivTableColumnInstance({
            title: t('modules.myOrders.header.type'),
            columnClass: 'type align-left',
            sortFunction: sortStringsBy(type, 'type'),
            sorted: getSorted('type'),
            sortDirection: getSortDirection('type'),
            hidden: false,
          }),
        ]
      : []),
    createDivTableColumnInstance({
      title: t('modules.myOrders.header.amount'),
      columnClass: 'amount align-left',
      sortFunction: sortBigNumberValuesBy(type, 'amount'),
      sorted: getSorted('amount'),
      sortDirection: getSortDirection('amount'),
      hidden: false,
    }),
    createDivTableColumnInstance({
      title: t('modules.myOrders.header.price'),
      columnClass: 'price align-left',
      sortFunction: sortByDisplayPrice(type),
      sorted: getSorted('price'),
      sortDirection: getSortDirection('price'),
      hidden: false,
    }),
    createDivTableColumnInstance({
      title: t('modules.myOrders.header.total'),
      columnClass: 'total align-left',
      sortFunction: sortBigNumberValuesBy(type, 'displayTotal'),
      sorted: getSorted('total'),
      sortDirection: getSortDirection('total'),
      hidden: false,
    }),
    ...(type === MyOrdersListType.open
      ? [
          createDivTableColumnInstance({
            title: t('modules.myOrders.header.status'),
            columnClass: 'status align-left',
            sortFunction: sortStringsBy(type, 'showAs'),
            sorted: getSorted('status'),
            sortDirection: getSortDirection('status'),
            hidden: false,
          }),
        ]
      : []),
  ]);

  return {
    desktopHeader,
    saveSorting,
    cancelOrder: ordersService.cancelOrder.bind(ordersService),
  };
};

export const getNumberOfActiveOrdersToCancel = () => {
  const activeOrdersToCancel = computed(
    () =>
      Object.values(ordersService.reactiveData[MyOrdersListType.open].value)
        .map(({ counter, orderId }) => {
          const makeVueAware = Number(counter >= 0);

          return ordersService.getOrderById(orderId, MyOrdersListType.open) || makeVueAware;
        })
        .filter((v) => {
          if (typeof v === 'number') {
            return false;
          } /** should never happen */

          return v.showAs === MyOrdersTabs.ACTIVE && !v.delayedRemove;
        }).length,
  );

  return activeOrdersToCancel;
};

export const useVirtualScroll = (dragging: Ref<boolean>, ordersDataToShow: ComputedRef<Array<unknown>>) => {
  const vListRef = ref();
  // @ts-ignore update is there :D
  const updateVirtualList = () => vListRef.value?.update();

  watch(ordersDataToShow, (newValue, oldValue) => {
    if (newValue.length !== oldValue.length) {
      updateVirtualList();
    }
  });

  watch(dragging, (newValue) => {
    if (!newValue) {
      updateVirtualList();
    }
  });

  return {
    vListRef,
  };
};
